<template>
  <div
    class="component pa-6"
    :style="{ backgroundImage: `url(${soccerField})` }"
  >
    <overlay
      :is-loading="isLoading"
    />
    <v-row
      no-gutters
      class="position"
    >
      <lineup-header
        :modelValue="lineup.formation"
        :players="players"
        :is-loading="isLoading"
        :lineup="lineup"
        :game-week="gameWeek"
        @update:modelValue="$emit('formationChange', $event)"
      />
      <v-row
        no-gutters
        class="position-row"
      >
        <div
          v-for="(forwardCount, index) in forwardsCount"
          :key="index"
        >
          <card-picker
            :card="forwardCard(index)"
            fixture="left"
            :show-score="!isOpen"
            :is-removable="isOpen"
            show-splitted-score
            :show-average-score="showAverageScore && isOpen"
            :is-pickable="isOpen"
            :is-captain="isCaptain(forwardCard(index))"
            :is-error="errorCards.includes(forwardCard(index))"
            :is-captainable="isOpen"
            width="100px"
            @openCardPick="openCardPick($consts.POSITION.FORWARD, index)"
            @captainSelected="captainSelected($event)"
            @removeCard="removeCard($consts.POSITION.FORWARD, index)"
          />
        </div>
      </v-row>
      <v-row
        no-gutters
        class="position-row"
      >
        <div
          v-for="(midfielderCount, index) in midfieldersCount"
          :key="index"
        >
          <card-picker
            :card="midfielderCard(index)"
            fixture="left"
            :show-score="!isOpen"
            :is-removable="isOpen"
            show-splitted-score
            :show-average-score="showAverageScore && isOpen"
            :is-pickable="isOpen"
            :is-captain="isCaptain(midfielderCard(index))"
            :is-error="errorCards.includes(midfielderCard(index))"
            :is-captainable="isOpen"
            width="100px"
            @openCardPick="openCardPick($consts.POSITION.MIDFIELDER, index)"
            @captainSelected="captainSelected($event)"
            @removeCard="removeCard($consts.POSITION.MIDFIELDER, index)"
          />
        </div>
      </v-row>
      <v-row
        no-gutters
        class="position-row"
      >
        <div
          v-for="(defenderCount, index) in defendersCount"
          :key="index"
        >
          <card-picker
            :card="defenderCard(index)"
            fixture="left"
            :show-score="!isOpen"
            :is-removable="isOpen"
            show-splitted-score
            :show-average-score="showAverageScore && isOpen"
            :is-pickable="isOpen"
            :is-captain="isCaptain(defenderCard(index))"
            :is-error="errorCards.includes(defenderCard(index))"
            :is-captainable="isOpen"
            width="100px"
            @openCardPick="openCardPick($consts.POSITION.DEFENDER, index)"
            @captainSelected="captainSelected($event)"
            @removeCard="removeCard($consts.POSITION.DEFENDER, index)"
          />
        </div>
      </v-row>
      <v-row
        no-gutters
        class="position-row"
      >
        <card-picker
          :card="goalkeeperCard()"
          fixture="left"
          :show-score="!isOpen"
          :is-removable="isOpen"
          show-splitted-score
          :show-average-score="showAverageScore && isOpen"
          :is-pickable="isOpen"
          :is-captain="isCaptain(goalkeeperCard())"
          :is-error="errorCards.includes(goalkeeperCard())"
          :is-captainable="isOpen"
          width="100px"
          @openCardPick="openCardPick($consts.POSITION.GOALKEEPER, 0)"
          @captainSelected="captainSelected($event)"
          @removeCard="removeCard($consts.POSITION.GOALKEEPER, 0)"
        />
      </v-row>
    </v-row>
    <pick-card-dialog
      v-model="displayPickCardDialog"
      :game-week-number="gameWeekNumber"
      :selected-cards="selectedCards"
      :show-average-score="showAverageScore"
      :forbidden-cards="forbiddenCards"
      @closeCardPick="closeCardPick"
      @cardPicked="cardPicked($event)"
    />
  </div>
</template>

<script>
import Overlay from '@/components/Overlay.vue';
import CardPicker from '@/components/Card/CardPicker.vue';
import PickCardDialog from '@/components/Card/PickCardDialog.vue';
import Lineup from '@/models/Lineup/lineup';
import GameWeek from '@/models/GameWeek/gameWeek';
import LineupHeader from "@/components/Lineup/LineupHeader.vue";
import soccerField from '@/assets/soccer-field.jpeg';
import { useCardFilterStore } from '@/store/cardFilter.module';

export default {
  name: 'Lineup',
  setup() {
    const cardFilterStore = useCardFilterStore();
    return { cardFilterStore };
  },
  components: {
    Overlay,
    CardPicker,
    PickCardDialog,
    LineupHeader,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    lineup: {
      type: Lineup,
      default: null,
    },
    gameWeek: {
      type: GameWeek,
      default: null,
    },
    showAverageScore: {
      type: Boolean,
      default: false,
    },
    forbiddenCards: {
      type: Array,
      default() {
        return [];
      },
    },
    errorCards: {
      type: Array,
      default() {
        return [];
      },
    },
    players: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      soccerField,
      displayPickCardDialog: false,
      selectingPosition: null,
      selectedCards: [],
    };
  },
  computed: {
    gameWeekNumber() {
      return this.gameWeek?.number;
    },
    isOpen() {
      return this.gameWeek?.isOpen() ?? false;
    },
    defendersCount() {
      return this.lineup?.defendersCount() ?? 0;
    },
    midfieldersCount() {
      return this.lineup?.midfieldersCount() ?? 0;
    },
    forwardsCount() {
      return this.lineup?.forwardsCount() ?? 0;
    }
  },
  methods: {
    forwardCard(index) {
      return this.lineup?.forwardsCard[index] ?? null;
    },
    midfielderCard(index) {
      return this.lineup?.midfieldersCard[index] ?? null;
    },
    defenderCard(index) {
      return this.lineup?.defendersCard[index] ?? null;
    },
    goalkeeperCard() {
      return this.lineup?.goalkeepersCard[0] ?? null;
    },
    openCardPick(position, index) {
      this.setSelectingPosition(position);
      this.selectingIndex = index;
      this.selectedCards = this.lineup[this.selectingPosition]?.flat() ?? [];
      this.cardFilterStore.reset();
      this.cardFilterStore.filter.property.position = [position];
      this.displayPickCardDialog = true;
    },
    closeCardPick() {
      this.displayPickCardDialog = false;
    },
    isCaptain(card) {
      return card?.slug === this.lineup?.captain;
    },
    cardPicked(card) {
      this.$emit('cardPicked', { card, position: this.selectingPosition, index: this.selectingIndex });
      this.displayPickCardDialog = false;
    },
    captainSelected(cardSlug) {
      this.$emit('captainSelected', cardSlug);
    },
    setSelectingPosition(position) {
      this.selectingPosition = `${position.toLowerCase()}sCard`;
    },
    removeCard(position, index) {
      this.$emit('cardRemoved', { position: `${position.toLowerCase()}sCard`, index });
    },
  },
};
</script>

<style scoped lang="scss">
  .component {
    background-size: cover;
    background-position: center;
    max-width: 1200px;
  }

  .position {
    flex-direction: column;
  }

  .position-row {
    justify-content: space-around;
    min-height: 214px
  }
</style>
